import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@apollo/client";
import {
  HStack,
  Text,
  IconButton,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormErrorMessage,
  InputGroup,
  InputLeftElement,
  Input,
  useToast,
} from "@chakra-ui/react";
import { FiGlobe, FiPlus, FiRefreshCw } from "react-icons/fi";
import { UPDATE_AGENCY_JOB_WEBSITE_AND_TRIGGER_SCRAPING } from "../graphql/agencies";
import { ADD_JOB_PATH, SYNC_JOBS_PATH } from "../routes";

function JobsPageHeader({ 
  title = "Jobs",
  showConnectButton = true,
  showNewJobButton = true,
  onConnectSuccess,
  ...props 
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [jobWebsiteUrl, setJobWebsiteUrl] = useState("");
  const [isValidUrl, setIsValidUrl] = useState(true);

  // Mutation for updating agency job website and triggering scraping
  const [updateAgencyJobWebsite, { loading: isUpdating }] = useMutation(
    UPDATE_AGENCY_JOB_WEBSITE_AND_TRIGGER_SCRAPING,
    {
      onCompleted: (data) => {
        toast({
          title: "Job website updated successfully",
          description: "Scraping has been triggered and you'll be redirected to the sync page.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        onClose();
        
        // Call custom success handler if provided
        if (onConnectSuccess) {
          onConnectSuccess(data);
        } else {
          // Default behavior: redirect to sync jobs page
          setTimeout(() => navigate(SYNC_JOBS_PATH), 1000);
        }
      },
      onError: (error) => {
        toast({
          title: "Failed to update job website",
          description: error.message || "An error occurred while updating the job website.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    }
  );

  // Function to validate URL
  const validateUrl = (url) => {
    try {
      new URL(url);
      return url.startsWith('http://') || url.startsWith('https://');
    } catch {
      return false;
    }
  };

  // Function to handle job website submission
  const handleJobWebsiteSubmit = async () => {
    if (!jobWebsiteUrl.trim()) {
      setIsValidUrl(false);
      return;
    }

    if (!validateUrl(jobWebsiteUrl)) {
      setIsValidUrl(false);
      return;
    }

    setIsValidUrl(true);

    try {
      await updateAgencyJobWebsite({
        variables: {
          jobWebsite: jobWebsiteUrl.trim(),
        },
      });
    } catch (error) {
      console.error('Error updating job website:', error);
    }
  };

  // Function to handle job website button click
  const handleJobWebsiteClick = () => {
    setJobWebsiteUrl("");
    setIsValidUrl(true);
    onOpen();
  };

  return (
    <>
      <HStack justify="space-between" width="100%" {...props} mb="15px">
        <Text fontSize="3xl" fontWeight="bold" color="white">
          {title}
        </Text>
        <HStack spacing={3}>
          {showConnectButton && (
            <IconButton
              icon={<FiGlobe />}
              variant="outline"
              colorScheme="gray"
              bg="gray.700"
              borderColor="gray.600"
              color="white"
              _hover={{ bg: "gray.600" }}
              aria-label="Connect Careers Site"
              onClick={handleJobWebsiteClick}
            />
          )}
          {showNewJobButton && (
            <Button
              leftIcon={<FiPlus />}
              colorScheme="gray"
              variant="solid"
              bg="gray.700"
              borderColor="gray.600"
              color="white"
              _hover={{ bg: "gray.600" }}
              onClick={() => navigate(ADD_JOB_PATH)}
            >
              New Job
            </Button>
          )}
        </HStack>
      </HStack>

      {/* Connect Careers Site Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay bg="blackAlpha.600" />
        <ModalContent bg="gray.800" color="white" borderRadius="xl">
          <ModalHeader pb={4}>
            <Text fontSize="xl" fontWeight="bold">
              Connect Careers Site
            </Text>
          </ModalHeader>
          <ModalCloseButton color="gray.400" />
          <ModalBody pb={6}>
            <Text color="gray.300" mb={6}>
              Add the URL of your company's careers page to start daily job syncing.
            </Text>
            <FormControl isInvalid={!isValidUrl}>
              <HStack spacing={0}>
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <FiGlobe color="gray.400" />
                  </InputLeftElement>
                  <Input
                    type="url"
                    placeholder="https://example.com/jobs"
                    value={jobWebsiteUrl}
                    onChange={(e) => {
                      setJobWebsiteUrl(e.target.value);
                      if (!isValidUrl) setIsValidUrl(true);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleJobWebsiteSubmit();
                      }
                    }}
                    bg="gray.700"
                    border="1px"
                    borderColor="gray.600"
                    borderRightRadius="none"
                    color="white"
                    _placeholder={{ color: "gray.400" }}
                    _focus={{
                      borderColor: "blue.500",
                      boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)"
                    }}
                  />
                </InputGroup>
                <Button
                  leftIcon={<FiRefreshCw />}
                  colorScheme="blue"
                  borderLeftRadius="none"
                  onClick={handleJobWebsiteSubmit}
                  isLoading={isUpdating}
                  loadingText="Syncing..."
                  px={6}
                >
                  Sync
                </Button>
              </HStack>
              {!isValidUrl && (
                <FormErrorMessage color="red.300" mt={2}>
                  Please enter a valid URL (must start with http:// or https://)
                </FormErrorMessage>
              )}
            </FormControl>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}

export default JobsPageHeader;
